import { useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useCurrentUser } from "../api/auth";
import { useUserRoles } from "../api/users";
import { AdminDashboard } from "./AdminDashboard";
import { CampaignsPanel } from "./CampaignsPanel";
import { DistributorsPanel } from "./DistributorsPanel";
import { UsersPanel } from "./UsersPanel";
import { AdminRegistrationsPanel } from "./AdminRegistrationsPanel";
import { DeliveryReportsPage } from "./DeliveryReportsPage";
import { BulkDistributionPanel } from "./BulkDistributionPanel";
import { HiddenAddressesPanel } from "./HiddenAddressesPanel";
import { UnderDeliveredAddressesPanel } from "./UnderDeliveredAddressesPanel";
import { DuplicateAddressCandidatesPage } from "./DuplicateAddressCandidatesPage";
import { ActivityReportsPage } from "./ActivityReportsPage";
import { PosterStatisticsPage } from "./PosterStatisticsPage";
import { PosterVandalismPage } from "./PosterVandalismPage";
import { PosterVandalismMapPage } from "./PosterVandalismMapPage";
import { ImportsPanel } from "./ImportsPanel";
import { HangfireJobsPanel } from "./HangfireJobsPanel";
import { AuditDashboard } from "./AuditDashboard";
import { SecurityDashboard } from "./SecurityDashboard";
import { AdminAssignmentsPage } from "./AdminAssignmentsPage";
import { AdminAssignmentsTablePage } from "./AdminAssignmentsTablePage";
import { KretsMembersPanel } from "./KretsMembersPanel";
import { RoReportingPage } from "./RoReportingPage";
import { MetadataFieldsPage } from "./MetadataFieldsPage";
import { MetadataGridPage } from "./MetadataGridPage";
import { getHangfireUrl } from "../api/hangfire";
import { useImportsAccess } from "../api/imports";
import { useElectoralMetadataFieldsAccess } from "../api/electoral-metadata/hooks";
import { WwwPortalNavBar } from "../app/WwwPortalNavBar";
import "./admin-layout.css";

/**
 * One nav entry. `exact` marks a route that only matches its own path (KPIs),
 * `alsoMatches` lists extra path prefixes that light the same entry, and
 * `external` marks a link that leaves the SPA (Hangfire Dashboard).
 */
type AdminNavItem = {
    to: string;
    label: string;
    exact?: boolean;
    alsoMatches?: string[];
    external?: boolean;
};

/**
 * Nav entries grouped by what they are used for. The groups are the menu at
 * every width: the bar holds only the current view, and the panel opens as a
 * grouped index — three columns on wide screens, one on small ones.
 */
type AdminNavGroup = {
    id: string;
    label: string;
    items: AdminNavItem[];
};

const ImportsRouteGuard = ({ children }: PropsWithChildren): JSX.Element => {
    const { data: importsAccess, isSuccess } = useImportsAccess();
    if (isSuccess && importsAccess === false) {
        return <Navigate to="/admin" replace />;
    }
    return <>{children}</>;
};

const MetadataFieldsRouteGuard = ({ children }: PropsWithChildren): JSX.Element => {
    const { data: fieldsAccess, isSuccess } = useElectoralMetadataFieldsAccess();
    if (isSuccess && fieldsAccess === false) {
        return <Navigate to="/admin/metadata-grid" replace />;
    }
    return <>{children}</>;
};

const SuperAdminRouteGuard = ({ children }: PropsWithChildren): JSX.Element => {
    const { data: currentUser } = useCurrentUser();
    const { data: roles = [], isSuccess } = useUserRoles(currentUser?.id ?? "");
    const isSuperAdmin = isSuccess && roles.some((r) => r.role === "SuperAdmin");
    if (isSuccess && !isSuperAdmin) return <Navigate to="/admin" replace />;
    return <>{children}</>;
};

export const AdminLayout = (): JSX.Element => {
    const location = useLocation();
    const { data: currentUser } = useCurrentUser();
    const { data: currentUserRoles = [] } = useUserRoles(currentUser?.id ?? "");
    const isSuperAdmin = currentUserRoles.some((r) => r.role === "SuperAdmin");
    const [menuOpen, setMenuOpen] = useState(false);
    const toggleRef = useRef<HTMLButtonElement>(null);

    // Fetch Hangfire URL - only shown if user has access
    const { data: hangfireUrl, isError: hangfireError } = useQuery({
        queryKey: ["hangfire", "url"],
        queryFn: getHangfireUrl,
        retry: false,
        staleTime: 5 * 60 * 1000 // 5 minutes
    });

    // Only show Imports link if user is SuperAdmin
    const { data: importsAccess } = useImportsAccess();

    const navGroups = useMemo<AdminNavGroup[]>(() => {
        const groups: AdminNavGroup[] = [
            {
                id: "overview",
                label: "Översikt",
                items: [{ to: "/admin", label: "KPIs", exact: true }]
            },
            {
                id: "addresses",
                label: "Adressdata",
                items: [
                    {
                        to: "/admin/metadata-grid",
                        label: "Valdistriktsmetadata",
                        alsoMatches: ["/admin/metadata-fields"]
                    },
                    { to: "/admin/hidden-addresses", label: "Dolda adresser" },
                    { to: "/admin/duplicate-candidates", label: "Adressdubbletter" }
                ]
            },
            {
                id: "campaign",
                label: "Kampanj",
                items: [
                    { to: "/admin/campaigns", label: "Kampanjer" },
                    { to: "/admin/activities", label: "Aktiviteter" }
                ]
            },
            {
                id: "people",
                label: "Personer",
                items: [
                    { to: "/admin/users", label: "Användare" },
                    { to: "/admin/distributors", label: "Distributörer" },
                    { to: "/admin/krets-members", label: "Utdelare" },
                    { to: "/admin/registrations", label: "Registreringar" }
                ]
            },
            {
                id: "distribution",
                label: "Utdelning",
                items: [
                    { to: "/admin/assignments", label: "Uppdrag" },
                    { to: "/admin/reports", label: "Utdelningar" },
                    { to: "/admin/bulk-distribution", label: "Massregistrering" },
                    { to: "/admin/under-delivered", label: "Underleveranser" },
                    { to: "/admin/ro-reporting", label: "RO-rapportering" },
                    { to: "/admin/posters", label: "Affischer" }
                ]
            }
        ];

        const systemItems: AdminNavItem[] = [];
        if (importsAccess === true) {
            systemItems.push({ to: "/admin/imports", label: "Importer" });
        }
        if (isSuperAdmin) {
            systemItems.push({ to: "/admin/audit", label: "Audit Log" });
            systemItems.push({ to: "/admin/security", label: "Security" });
        }
        if (hangfireUrl && !hangfireError) {
            systemItems.push({ to: "/admin/hangfire-jobs", label: "Hangfire-jobb" });
            systemItems.push({ to: hangfireUrl, label: "Hangfire Dashboard", external: true });
        }
        if (systemItems.length > 0) {
            groups.push({ id: "system", label: "System", items: systemItems });
        }

        return groups;
    }, [importsAccess, isSuperAdmin, hangfireUrl, hangfireError]);

    // Longest matching prefix wins, so /admin/posters/vandalism stays on Affischer.
    const activeItem = useMemo<AdminNavItem | null>(() => {
        const path = location.pathname.replace(/\/+$/, "") || "/admin";
        let best: AdminNavItem | null = null;
        let bestLength = 0;
        for (const group of navGroups) {
            for (const item of group.items) {
                if (item.external) continue;
                if (item.exact) {
                    if (path === item.to) return item;
                    continue;
                }
                for (const prefix of [item.to, ...(item.alsoMatches ?? [])]) {
                    const matches = path === prefix || path.startsWith(`${prefix}/`);
                    if (matches && prefix.length > bestLength) {
                        best = item;
                        bestLength = prefix.length;
                    }
                }
            }
        }
        return best;
    }, [navGroups, location.pathname]);

    // Named above the current view, so the bar also says which part of admin you are in.
    const activeGroup = useMemo<AdminNavGroup | null>(
        () => navGroups.find((group) => group.items.some((item) => item === activeItem)) ?? null,
        [navGroups, activeItem]
    );

    // The panel is a menu: navigating away or pressing Escape closes it.
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!menuOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setMenuOpen(false);
                toggleRef.current?.focus();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [menuOpen]);

    const handleHangfireClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (hangfireUrl) {
            e.preventDefault();
            window.open(hangfireUrl, "_blank", "noopener,noreferrer");
        }
    };

    // Only the map view fills the viewport; the printable table keeps normal page padding.
    const isFullBleed =
        location.pathname.startsWith("/admin/assignments") &&
        !location.pathname.startsWith("/admin/assignments/table");

    return (
        <div className="admin-layout-root">
            <div className="admin-layout__portal-nav">
                <WwwPortalNavBar surface="www_other" />
            </div>
            <div className="admin-layout">
            <nav className="admin-layout__nav" role="navigation" aria-label="Admin navigation">
                <Link
                    to="/"
                    className="admin-layout__nav-link admin-layout__nav-link--back"
                    title="Tillbaka till karta"
                >
                    ← Karta
                </Link>
                <button
                    ref={toggleRef}
                    type="button"
                    className="admin-layout__nav-toggle"
                    aria-expanded={menuOpen}
                    aria-controls="admin-nav-panel"
                    onClick={() => setMenuOpen((open) => !open)}
                >
                    <span className="admin-layout__nav-toggle-text">
                        <span className="admin-layout__nav-toggle-eyebrow">{activeGroup?.label ?? "Admin"}</span>
                        <span className="admin-layout__nav-toggle-label">{activeItem?.label ?? "Välj vy"}</span>
                    </span>
                    <span className="admin-layout__nav-toggle-chevron" aria-hidden="true" />
                </button>
                {menuOpen && (
                    <button
                        type="button"
                        className="admin-layout__nav-scrim"
                        aria-label="Stäng menyn"
                        onClick={() => setMenuOpen(false)}
                    />
                )}
                <div
                    id="admin-nav-panel"
                    className="admin-layout__nav-panel"
                    data-open={menuOpen ? "true" : "false"}
                >
                    {navGroups.map((group) => (
                        <div key={group.id} className="admin-layout__nav-group">
                            <span className="admin-layout__nav-group-label">{group.label}</span>
                            {group.items.map((item) =>
                                item.external ? (
                                    <a
                                        key={item.label}
                                        href={item.to}
                                        onClick={handleHangfireClick}
                                        className="admin-layout__nav-link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Öppna Hangfire Dashboard i ny flik"
                                    >
                                        {item.label}
                                    </a>
                                ) : (
                                    <Link
                                        key={item.to}
                                        to={item.to}
                                        className={`admin-layout__nav-link ${activeItem === item ? "active" : ""}`}
                                    >
                                        {item.label}
                                    </Link>
                                )
                            )}
                        </div>
                    ))}
                </div>
            </nav>

            <main className={`admin-layout__content${isFullBleed ? " admin-layout__content--full-bleed" : ""}`}>
                <Routes>
                    <Route index element={<AdminDashboard />} />
                    <Route path="campaigns" element={<CampaignsPanel />} />
                    <Route path="users" element={<UsersPanel />} />
                    <Route path="distributors" element={<DistributorsPanel />} />
                    <Route path="assignments" element={<AdminAssignmentsPage />} />
                    <Route path="assignments/table" element={<AdminAssignmentsTablePage />} />
                    <Route path="krets-members" element={<KretsMembersPanel />} />
                    <Route path="reports" element={<DeliveryReportsPage />} />
                    <Route path="bulk-distribution" element={<BulkDistributionPanel />} />
                    <Route path="hidden-addresses" element={<HiddenAddressesPanel />} />
                    <Route path="under-delivered" element={<UnderDeliveredAddressesPanel />} />
                    <Route path="duplicate-candidates" element={<DuplicateAddressCandidatesPage />} />
                    <Route path="ro-reporting" element={<RoReportingPage />} />
                    <Route
                        path="metadata-fields"
                        element={
                            <MetadataFieldsRouteGuard>
                                <MetadataFieldsPage />
                            </MetadataFieldsRouteGuard>
                        }
                    />
                    <Route path="metadata-grid" element={<MetadataGridPage />} />
                    <Route path="posters" element={<PosterStatisticsPage />} />
                    <Route path="posters/vandalism" element={<PosterVandalismPage />} />
                    <Route path="posters/vandalism/map" element={<PosterVandalismMapPage />} />
                    <Route path="activities" element={<ActivityReportsPage />} />
                    <Route path="registrations" element={<AdminRegistrationsPanel />} />
                    <Route
                        path="imports"
                        element={
                            <ImportsRouteGuard>
                                <ImportsPanel />
                            </ImportsRouteGuard>
                        }
                    />
                    <Route
                        path="audit"
                        element={
                            <SuperAdminRouteGuard>
                                <AuditDashboard />
                            </SuperAdminRouteGuard>
                        }
                    />
                    <Route
                        path="security"
                        element={
                            <SuperAdminRouteGuard>
                                <SecurityDashboard />
                            </SuperAdminRouteGuard>
                        }
                    />
                    <Route path="hangfire-jobs" element={<HangfireJobsPanel />} />
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                </Routes>
            </main>
        </div>
        </div>
    );
};
